// src/hooks/useNotifications.ts
import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/Firebase/Firebase';
import { useAuth } from '@/Context/AuthContext';
import type { NotificationItem } from '@/types/notification';

export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'Customers', user.uid, 'Notifications'),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          type: data.type,
          title: data.title,
          // Cloud Functions écrit "body", anciens docs écrivent "message"
          message: data.message ?? data.body ?? '',
          orderId: data.orderId ?? null,
          claimId: data.claimId ?? null,
          read: data.read ?? false,
          createdAt: data.createdAt instanceof Timestamp
            ? data.createdAt.toDate()
            : new Date(data.createdAt ?? Date.now()),
        } as NotificationItem;
      });
      setNotifications(items);
      setLoading(false);
    });

    return unsub;
  }, [user?.uid]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = async (notificationId: string) => {
    if (!user?.uid) return;
    try {
      await updateDoc(
        doc(db, 'Customers', user.uid, 'Notifications', notificationId),
        { read: true }
      );
    } catch (err) {
      console.error('[notifications] markAsRead failed:', notificationId, err);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.uid) return;
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;

    const uid = user.uid;
    // allSettled : chaque update est tenté même si un autre échoue
    const results = await Promise.allSettled(
      unread.map((n) =>
        updateDoc(
          doc(db, 'Customers', uid, 'Notifications', n.id),
          { read: true }
        )
      )
    );

    // Logge les éventuels échecs + réessaie une fois (token refresh race)
    const failures = results
      .map((r, i) => (r.status === 'rejected' ? { n: unread[i], reason: r.reason } : null))
      .filter(Boolean) as { n: NotificationItem; reason: unknown }[];

    if (failures.length > 0) {
      failures.forEach(({ n, reason }) =>
        console.error('[notifications] markAllAsRead failed for', n.id, reason)
      );
      const retryResults = await Promise.allSettled(
        failures.map(({ n }) =>
          updateDoc(
            doc(db, 'Customers', uid, 'Notifications', n.id),
            { read: true }
          )
        )
      );
      retryResults.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error('[notifications] retry failed for', failures[i].n.id, r.reason);
        }
      });
    }
  };

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead };
};
